import type { RegistrationStep } from './screenName';
import { REGISTRATION_STEP_SET } from './screenName';

export type CustomerType = string;
export type VerificationMethod = 'sumsub' | 'imID';
export type OtpType = 'email' | 'phone' | 'contract';

export type RegistrationStateKey =
    | 'customerType'
    | 'verificationMethod'
    | 'lastStep'
    | 'startedAt'
    | 'contractStartedAt'
    | 'biometricStartedAt'
    | 'otpStartedAt.email'
    | 'otpStartedAt.phone'
    | 'otpStartedAt.contract'
    | 'completed'
    | 'failedTracked'
    | 'abandonedTracked';

type RegistrationStateValues = {
    customerType: CustomerType | null;
    verificationMethod: VerificationMethod | null;
    lastStep: RegistrationStep | null;
    startedAt: number | null;
    contractStartedAt: number | null;
    biometricStartedAt: number | null;
    'otpStartedAt.email': number | null;
    'otpStartedAt.phone': number | null;
    'otpStartedAt.contract': number | null;
    completed: boolean;
    failedTracked: boolean;
    abandonedTracked: boolean;
};

type TimestampKey =
    | 'startedAt'
    | 'contractStartedAt'
    | 'biometricStartedAt'
    | 'otpStartedAt.email'
    | 'otpStartedAt.phone'
    | 'otpStartedAt.contract';

type FlagKey = 'completed' | 'failedTracked' | 'abandonedTracked';

const STORAGE_KEY: Record<RegistrationStateKey, string> = {
    customerType: 'bioweb_analytics_customer_type',
    verificationMethod: 'bioweb_analytics_verification_method',
    lastStep: 'bioweb_analytics_last_step',
    startedAt: 'bioweb_analytics_started_at',
    contractStartedAt: 'bioweb_analytics_contract_started_at',
    biometricStartedAt: 'bioweb_analytics_biometric_started_at',
    'otpStartedAt.email': 'bioweb_analytics_otp_started_at_email',
    'otpStartedAt.phone': 'bioweb_analytics_otp_started_at_phone',
    'otpStartedAt.contract': 'bioweb_analytics_otp_started_at_contract',
    completed: 'bioweb_analytics_completed',
    failedTracked: 'bioweb_analytics_failed_tracked',
    abandonedTracked: 'bioweb_analytics_abandoned_tracked',
};

const TIMESTAMP_KEYS = new Set<RegistrationStateKey>([
    'startedAt',
    'contractStartedAt',
    'biometricStartedAt',
    'otpStartedAt.email',
    'otpStartedAt.phone',
    'otpStartedAt.contract',
]);

const FLAG_KEYS = new Set<RegistrationStateKey>([
    'completed',
    'failedTracked',
    'abandonedTracked',
]);

function readString(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeString(key: string, value: string | null): void {
    try {
        if (value == null) {
            sessionStorage.removeItem(key);
        } else {
            sessionStorage.setItem(key, value);
        }
    } catch {
        // sessionStorage may be unavailable
    }
}

function readTs(storageKey: string): number | null {
    const raw = readString(storageKey);
    if (!raw) {
        return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

function toCustomerTypeValue(value: unknown): string | null {
    if (value == null) {
        return null;
    }
    return typeof value === 'string' ? value : String(value);
}

function normalizeVerificationMethod(
    value: unknown
): VerificationMethod | null {
    if (value === 'sumsub') {
        return 'sumsub';
    }
    if (value === 'imID' || value === 'imid' || value === 'imId') {
        return 'imID';
    }
    return null;
}

function otpStartedKey(otpType: OtpType): `otpStartedAt.${OtpType}` {
    return `otpStartedAt.${otpType}`;
}

export function durationSecSince(
    startedAt: number | null,
    end = Date.now()
): number | null {
    if (startedAt == null) {
        return null;
    }
    return Math.max(0, Math.round((end - startedAt) / 1000));
}

/**
 * Session-backed registration analytics store (internal).
 * UI code should use the semantic helpers below.
 */
export const registrationState = {
    get<K extends RegistrationStateKey>(key: K): RegistrationStateValues[K] {
        const storageKey = STORAGE_KEY[key];

        if (TIMESTAMP_KEYS.has(key)) {
            return readTs(storageKey) as RegistrationStateValues[K];
        }

        if (FLAG_KEYS.has(key)) {
            return (readString(storageKey) === '1') as RegistrationStateValues[K];
        }

        if (key === 'customerType') {
            return readString(storageKey) as RegistrationStateValues[K];
        }

        if (key === 'verificationMethod') {
            return normalizeVerificationMethod(
                readString(storageKey)
            ) as RegistrationStateValues[K];
        }

        if (key === 'lastStep') {
            const raw = readString(storageKey);
            if (raw && REGISTRATION_STEP_SET.has(raw as RegistrationStep)) {
                return raw as RegistrationStateValues[K];
            }
            return null as RegistrationStateValues[K];
        }

        return null as RegistrationStateValues[K];
    },

    set<K extends RegistrationStateKey>(
        key: K,
        value?: RegistrationStateValues[K] | unknown,
        options?: { onlyIfAbsent?: boolean }
    ): void {
        const storageKey = STORAGE_KEY[key];

        if (TIMESTAMP_KEYS.has(key)) {
            if (options?.onlyIfAbsent && readString(storageKey)) {
                return;
            }
            const at = typeof value === 'number' ? value : Date.now();
            writeString(storageKey, String(at));
            return;
        }

        if (FLAG_KEYS.has(key)) {
            writeString(storageKey, value === false ? null : '1');
            return;
        }

        if (key === 'customerType') {
            writeString(storageKey, toCustomerTypeValue(value));
            return;
        }

        if (key === 'verificationMethod') {
            writeString(storageKey, normalizeVerificationMethod(value));
            return;
        }

        if (key === 'lastStep') {
            if (
                typeof value === 'string' &&
                REGISTRATION_STEP_SET.has(value as RegistrationStep)
            ) {
                writeString(storageKey, value);
            }
            return;
        }
    },

    durationSec(key: TimestampKey, end = Date.now()): number {
        return durationSecSince(this.get(key), end) ?? 0;
    },

    clear(): void {
        (Object.keys(STORAGE_KEY) as RegistrationStateKey[]).forEach((key) => {
            writeString(STORAGE_KEY[key], null);
        });
    },
};

// --- Public semantic API (pages should use these, not storage keys) ---

export function setAnalyticsCustomerType(value: unknown): void {
    registrationState.set('customerType', value);
}

export function getAnalyticsCustomerType(): CustomerType | null {
    return registrationState.get('customerType');
}

export function setAnalyticsVerificationMethod(value: unknown): void {
    registrationState.set('verificationMethod', value);
}

export function getAnalyticsVerificationMethod(): VerificationMethod | null {
    return registrationState.get('verificationMethod');
}

export function setLastRegistrationStep(step: RegistrationStep | null): void {
    if (!step) {
        return;
    }
    registrationState.set('lastStep', step);
}

export function getLastRegistrationStep(): RegistrationStep | null {
    return registrationState.get('lastStep');
}

/** Stores Date.now(); does not overwrite if already set. */
export function markRegistrationStarted(at = Date.now()): void {
    registrationState.set('startedAt', at, { onlyIfAbsent: true });
}

export function getRegistrationStartedAt(): number | null {
    return registrationState.get('startedAt');
}

export function markContractStarted(at = Date.now()): void {
    registrationState.set('contractStartedAt', at);
}

export function getContractStartedAt(): number | null {
    return registrationState.get('contractStartedAt');
}

export function markBiometricStarted(at = Date.now()): void {
    registrationState.set('biometricStartedAt', at);
}

export function getBiometricStartedAt(): number | null {
    return registrationState.get('biometricStartedAt');
}

export function markOtpStarted(otpType: OtpType, at = Date.now()): void {
    registrationState.set(otpStartedKey(otpType), at);
}

export function getOtpStartedAt(otpType: OtpType): number | null {
    return registrationState.get(otpStartedKey(otpType));
}

export function markRegistrationCompleted(): void {
    registrationState.set('completed');
}

export function isRegistrationCompleted(): boolean {
    return registrationState.get('completed');
}

export function markRegistrationFailedTracked(): void {
    registrationState.set('failedTracked');
}

export function isRegistrationFailedTracked(): boolean {
    return registrationState.get('failedTracked');
}

export function markRegistrationAbandonedTracked(): void {
    registrationState.set('abandonedTracked');
}

export function isRegistrationAbandonedTracked(): boolean {
    return registrationState.get('abandonedTracked');
}

export function clearAnalyticsRegistrationMeta(): void {
    registrationState.clear();
}
